import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useSeo, useUpsertSeo } from "@/features/pages-seo/queries";
import MediaPicker from "../Media/MediaPicker";
import { GooglePreview } from "./GooglePreview";
import type { Lang } from "./LangToggle";
import { cn } from "@/lib/utils";
import { errorMessage } from "@/lib/error-message";

// Boş JSON-LD geçerlidir (alan opsiyonel); doluysa geçerli JSON olmalı.
function jsonLdErrorOf(text: string): string | null {
  if (!text.trim()) return null;
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed === null || typeof parsed !== "object") {
      return "JSON-LD bir nesne ya da dizi olmalı.";
    }
    return null;
  } catch (e) {
    return `Geçersiz JSON: ${e instanceof Error ? e.message : "ayrıştırılamadı"}`;
  }
}

interface SeoForm {
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  ogImage: string;
  canonical: string;
  noindex: boolean;
  jsonLd: string;
}

const DEFAULT_FORM: SeoForm = {
  metaTitle: "",
  metaDescription: "",
  keywords: "",
  ogImage: "",
  canonical: "",
  noindex: false,
  jsonLd: "",
};

export function SeoPanel({
  path,
  previewPath,
  locale,
}: {
  // SeoMeta anahtarı — iki dilde de TR yolu
  path: string;
  // Önizleme URL'i için yol (EN slug farklı olabilir); verilmezse path
  previewPath?: string;
  locale: Lang;
}) {
  const { data } = useSeo(path, locale);
  const upsert = useUpsertSeo();

  const [form, setForm] = useState<SeoForm>(DEFAULT_FORM);
  const [saved, setSaved] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        metaTitle: data.metaTitle ?? "",
        metaDescription: data.metaDescription ?? "",
        keywords: data.keywords ?? "",
        ogImage: data.ogImage ?? "",
        canonical: data.canonical ?? "",
        noindex: data.noindex ?? false,
        jsonLd: data.jsonLd ? JSON.stringify(data.jsonLd, null, 2) : "",
      });
    } else {
      setForm(DEFAULT_FORM);
    }
  }, [data, locale]);

  function set<K extends keyof SeoForm>(key: K, value: SeoForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  const jsonLdError = jsonLdErrorOf(form.jsonLd);

  function handleSave() {
    if (jsonLdError) {
      toast.error("JSON-LD geçersiz; düzeltmeden kaydedilemez.");
      document.getElementById("seo-jsonld")?.focus();
      return;
    }
    const payload = {
      path,
      locale,
      metaTitle: form.metaTitle || null,
      metaDescription: form.metaDescription || null,
      keywords: form.keywords || null,
      ogImage: form.ogImage || null,
      canonical: form.canonical || null,
      noindex: form.noindex,
      jsonLd: form.jsonLd.trim() ? (JSON.parse(form.jsonLd) as unknown) : null,
    };

    upsert.mutate(payload, {
      onSuccess: () => {
        setSaved(true);
        toast.success("SEO ayarları kaydedildi.");
      },
      onError: (error) => {
        toast.error(errorMessage(error, "SEO kaydedilirken hata oluştu."));
      },
    });
  }

  const titleLen = form.metaTitle.length;
  const descLen = form.metaDescription.length;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Boş bırakılan alanlar için site içerikten otomatik SEO üretir.
      </p>

      <div className="space-y-4">
        {/* Meta Title */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="seo-meta-title">Meta Başlık</Label>
            <span
              className={cn(
                "text-xs",
                titleLen > 60 ? "text-destructive font-medium" : "text-muted-foreground",
              )}
            >
              {titleLen}/60
            </span>
          </div>
          <Input
            id="seo-meta-title"
            value={form.metaTitle}
            onChange={(e) => set("metaTitle", e.target.value)}
            placeholder="Sayfa başlığı (60 karakter önerilir)"
          />
          {titleLen > 60 && (
            <p className="text-xs text-destructive">
              Başlık 60 karakteri aşıyor — arama sonuçlarında kesilebilir.
            </p>
          )}
        </div>

        {/* Meta Description */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="seo-meta-desc">Meta Açıklama</Label>
            <span
              className={cn(
                "text-xs",
                descLen > 160 ? "text-destructive font-medium" : "text-muted-foreground",
              )}
            >
              {descLen}/160
            </span>
          </div>
          <Textarea
            id="seo-meta-desc"
            value={form.metaDescription}
            onChange={(e) => set("metaDescription", e.target.value)}
            placeholder="Sayfa açıklaması (160 karakter önerilir)"
            rows={3}
          />
          {descLen > 160 && (
            <p className="text-xs text-destructive">
              Açıklama 160 karakteri aşıyor — arama sonuçlarında kesilebilir.
            </p>
          )}
        </div>

        {/* Keywords */}
        <div className="space-y-1.5">
          <Label htmlFor="seo-keywords">Anahtar Kelimeler</Label>
          <Input
            id="seo-keywords"
            value={form.keywords}
            onChange={(e) => set("keywords", e.target.value)}
            placeholder="virgülle ayrılmış, anahtar, kelimeler"
          />
        </div>

        {/* OG Image */}
        <div className="space-y-1.5">
          <Label>OG Görsel</Label>
          <div className="flex gap-2">
            <Input
              value={form.ogImage}
              readOnly
              placeholder="Medya kütüphanesinden görsel seçin"
              className="flex-1"
            />
            <Button type="button" variant="outline" onClick={() => setMediaOpen(true)}>
              Medyadan Seç
            </Button>
            {form.ogImage && (
              <Button type="button" variant="destructive" onClick={() => set("ogImage", "")}>
                Kaldır
              </Button>
            )}
          </div>
          {form.ogImage && (
            <img
              src={form.ogImage}
              alt="OG görsel önizleme"
              className="max-h-32 max-w-xs object-contain rounded border"
            />
          )}
        </div>

        {/* Canonical */}
        <div className="space-y-1.5">
          <Label htmlFor="seo-canonical">Canonical URL</Label>
          <Input
            id="seo-canonical"
            value={form.canonical}
            onChange={(e) => set("canonical", e.target.value)}
            placeholder="https://endeksbilisim.com/..."
          />
        </div>

        {/* Noindex */}
        <div className="flex items-center gap-3">
          <Switch
            id="seo-noindex"
            checked={form.noindex}
            onCheckedChange={(v) => set("noindex", v)}
          />
          <Label htmlFor="seo-noindex">Arama motorlarından gizle (noindex)</Label>
        </div>

        {/* JSON-LD */}
        <div className="space-y-1.5">
          <Label htmlFor="seo-jsonld">JSON-LD (opsiyonel)</Label>
          <Textarea
            id="seo-jsonld"
            value={form.jsonLd}
            onChange={(e) => set("jsonLd", e.target.value)}
            placeholder='{ "@context": "https://schema.org", ... }'
            rows={4}
            className={cn("font-mono text-xs", jsonLdError && "border-destructive")}
            aria-invalid={jsonLdError ? true : undefined}
            aria-describedby={jsonLdError ? "seo-jsonld-error" : undefined}
          />
          {jsonLdError && (
            <p id="seo-jsonld-error" className="text-xs text-destructive">
              {jsonLdError}
            </p>
          )}
        </div>
      </div>

      {/* Google Preview */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Google Önizleme
        </p>
        <GooglePreview
          title={form.metaTitle || "(başlık yok)"}
          description={form.metaDescription}
          path={previewPath ?? path}
          locale={locale}
        />
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={upsert.isPending || !!jsonLdError}>
          {upsert.isPending ? "Kaydediliyor…" : "SEO Kaydet"}
        </Button>
        {saved && !upsert.isPending && <span className="text-sm text-green-600">Kaydedildi</span>}
      </div>

      <MediaPicker
        defaultFolder="content"
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onSelect={(url) => {
          set("ogImage", url);
          setMediaOpen(false);
        }}
      />
    </div>
  );
}
